import { useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { RuntimeContext } from 'src/runtimeContext.ts';
import { useUserInputState } from 'src/actuators/user-input/useUserInputState.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { ACTUATORS_DECIMAL_DIGITS, MESSAGES } from 'src/modules/common/constants.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import {
    AirflowEnum,
    DamperBlades,
    DamperType,
    initialState as InitState,
    RoundDamperBlades,
} from 'src/actuators/user-input/types.ts';
import { CalculatorLayout } from 'src/modules/common/layouts/CalculatorLayout.tsx';
import { CalculatorLeftColumn } from 'src/modules/common/components/CalculatorLeftColumn/CalculatorLeftColumn.tsx';
import { CalculatorRightColumn } from 'src/modules/common/components/CalculatorRightColumn/CalculatorRightColumn.tsx';
import { RadioButton } from 'src/modules/common/components/Radio/RadioButton.tsx';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { Units } from 'uom-units';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField.tsx';
import { RegionsEnum } from 'src/generated/operations.ts';
import {
    ActuatorsAppUserInputPageData,
    getUserInputPageConfig,
} from 'src/actuators/user-input/userInputPageMapping.ts';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { Tooltip } from 'src/modules/common/components/Tooltip/Tooltip';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { ActuatorTypes } from 'src/actuators/user-input/calculations/constants.ts';
import { detectActuatorType } from 'src/actuators/detectActuatorType.ts';
import { detectTargetUnit } from 'src/actuators/detectTargetUnit.ts';
import { useReadUserInput } from 'src/actuators/user-input/useReadUserInput.ts';
import { Button } from 'src/common/Button/Button';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification.tsx';
import { AirflowTooltipContent } from 'src/actuators/user-input/components/AirflowTooltipContent';
import { SafetyTooltipContent } from 'src/actuators/user-input/components/SafetyTooltipContent';
import { AreaTooltipContent } from 'src/actuators/user-input/components/AreaTooltipContent';
import { areaFieldResultAm, areaFieldResultEmea } from 'src/actuators/user-input/utils/areaFieldResult';
import { diameterFieldResultAm } from 'src/actuators/user-input/utils/diameterFieldResult';
import { mapBladeTypeNames } from 'src/actuators/constants';
import { getActuatorsListPageConfig } from 'src/actuators/list/ActuatorsListPageMapping.ts';
import { MapFieldToLabel } from 'src/actuators/common/UserInputFields.ts';
import { useGetHomeBreadcrumb } from 'src/actuators/home/useGetHomeBreadcrumb.ts';
import { ActuatorBreadcrumbs } from 'src/actuators/common/ActuatorBreadcrumbs/ActuatorBreadcrumbs';
import { PATHS } from 'src/actuators/common/types/Paths';
import { useActuatorCheckUserInputValid } from 'src/actuators/common/hooks/useActuatorCheckUserInputValid';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';

const damperBlades = [
    DamperBlades.ParallelBladeWithSeals,
    DamperBlades.ParallelBladeWithoutSeals,
    DamperBlades.OpposedBladeWithSeals,
    DamperBlades.OpposedBladeWithoutSeals,
];

export const UserInputPage = ({ data } : { data: ActuatorsAppUserInputPageData }) => {
    const { userSettings } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const navigate = useNavigate();
    const pageConfig = useMemo(() => getUserInputPageConfig(userSettings.currentRegion), [userSettings]);
    const actuatorsPageConfig = useMemo(() => getActuatorsListPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }
    if (actuatorsPageConfig === null) {
        throw new Error('No Page Config!');
    }

    const [search] = useSearchParams();
    const [filter] = useFilterLogic(pageConfig.filterConfig, search);

    const [selectedActuatorType, targetUnit] = useMemo(
        () => {
            const type = detectActuatorType(filter, navigate);
            return [type, detectTargetUnit(type, userSettings.currentRegion)];
        },
        [filter, navigate, userSettings.currentRegion],
    );

    const isRoundAllowed = !(userSettings.currentRegion !== RegionsEnum.Am &&
        selectedActuatorType === ActuatorTypes.Linear);

    const initialState = useMemo(() => ({
        ...InitState,
        ...(isRoundAllowed ? {} : { damper: DamperType.Rectangular }),
    }), [isRoundAllowed]);

    const {
        state,
        searchParams,
        setState: setInputState,
        locationState,
    } = useUserInputState(selectedActuatorType, initialState, actuatorsPageConfig.filterConfig, data);

    const { state: userInputState } = useReadUserInput(searchParams, userSettings.currentRegion);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    useCheckMainFilter({
        filter,
        navigate,
        searchParams,
        filterConfig: [],
    });

    const areAnyFieldsFilled = Boolean(userInputState.areaOrDiameter) ||
            userInputState.blade !== null;

    const { isFormFilled, emptyResult } = useActuatorCheckUserInputValid({
        selectedActuatorType,
        state,
        searchParams,
        currentRegion: userSettings.currentRegion,
    });

    const areaFieldResult = useCallback((areaWidth: string, areaHeight: string): void => {
        try {
            if (userSettings.currentRegion === RegionsEnum.Am) {
                return setInputState({
                    ...state,
                    height: areaHeight,
                    width: areaWidth,
                    areaOrDiameter: areaFieldResultAm(areaWidth, areaHeight),
                    force: null,
                });
            }

            return setInputState({
                ...state,
                height: areaHeight,
                width: areaWidth,
                areaOrDiameter: areaFieldResultEmea(areaWidth, areaHeight),
                force: null,
            });
        } catch {
            return setInputState({
                ...state,
                areaOrDiameter: '',
                force: null,
            });
        }
    }, [userSettings.currentRegion, setInputState, state]);

    const diameterFieldResult = useCallback((value: string): void => {
        try {
            return setInputState({
                ...state,
                diameter: value,
                areaOrDiameter: diameterFieldResultAm(value),
            });
        } catch {
            return setInputState({
                ...state,
                areaOrDiameter: '',
                force: null,
            });
        }
    }, [state, setInputState]);

    const handleReset = useCallback(() => {
        setInputState(null);
    }, [setInputState]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <Button
                kind="ghost-secondary"
                size={isMobile ? 'md' : 'sm'}
                onClick={handleReset}
                fullWidth={isMobile}
            >
                <I18n messageId={MESSAGES.Reset}/>
                <LoadingIcon/>
            </Button>
            <NavigationButton
                type="forward"
                state={locationState}
                to={{
                    pathname: `${PATHS.Actuators}/1`,
                    search: searchParams.toString(),
                }}
                disabled={emptyResult || !isFormFilled}
                isMobile={isMobile}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    return (
        <div className="vss-actuators">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.actuatorsUserInputPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <ActuatorBreadcrumbs
                            currentPage={PATHS.UserInput}
                            homePageValue={homePageValue}
                            isLinear={selectedActuatorType === ActuatorTypes.Linear}
                            userInputState={userInputState}
                            isUserInputPageInvalid={emptyResult || !isFormFilled}
                            resetHandler={areAnyFieldsFilled ? handleReset : undefined}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                <div className="vss-actuators-user-input-page">
                    {emptyResult && (
                        <InlineNotification
                            type="error"
                            message={<I18n messageId={MESSAGES.WE_RE_SORRY}/>}
                        />
                    )}
                    <CalculatorLayout>
                        <CalculatorLeftColumn>
                            <div className="vss-actuators-user-input-page__damper-section">
                                <div className="vss-actuators-user-input-page__checkbox-section">
                                    <p className="vss-actuators-user-input-page__checkbox-title">
                                        <I18n messageId={MapFieldToLabel.damper}/>
                                    </p>

                                    <div className="vss-actuators-user-input-page__checkbox-row">
                                        <RadioButton
                                            name="damper"
                                            id={`damper${DamperType.Rectangular}`}
                                            value={DamperType.Rectangular}
                                            checked={state.damper === DamperType.Rectangular}
                                            onChange={() => {
                                                setInputState(null);
                                                setInputState({
                                                    ...initialState,
                                                    damper: DamperType.Rectangular,
                                                    force: null,
                                                });
                                            }}
                                        >
                                            <I18n messageId={MESSAGES.DamperRectangular}/>
                                        </RadioButton>

                                        {isRoundAllowed && (
                                            <RadioButton
                                                name="damper"
                                                id={`damper${DamperType.Round}`}
                                                value={DamperType.Round}
                                                checked={state.damper === DamperType.Round}
                                                onChange={() => {
                                                    setInputState(null);
                                                    setInputState({
                                                        ...initialState,
                                                        damper: DamperType.Round,
                                                        force: null,
                                                    });
                                                }}
                                            >
                                                <I18n messageId={MESSAGES.DamperRound}/>
                                            </RadioButton>
                                        )}
                                    </div>
                                </div>

                                {state.damper === DamperType.Rectangular && (
                                    <>
                                        <DecimalField
                                            name="height"
                                            inputSize="md"
                                            maxValue={2000}
                                            labelText={userSettings.currentRegion === RegionsEnum.Am
                                                ? MESSAGES.DamperHeightAm
                                                : MESSAGES.DamperHeight}
                                            value={state.height}
                                            shaded={false}
                                            endAdornment={(
                                                <UnitSelector
                                                    selected={userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.Inch.name
                                                        : Units.Millimeter.name}
                                                    shaded={false}
                                                    values={[
                                                        userSettings.currentRegion === RegionsEnum.Am
                                                            ? Units.Inch
                                                            : Units.Millimeter,
                                                    ]}
                                                    onChange={() => null}
                                                />
                                            )}
                                            currentLocale={userSettings.currentLocale}
                                            onValueChange={(payload) => {
                                                areaFieldResult(state.width, payload);
                                            }}
                                        />
                                        <DecimalField
                                            name="width"
                                            inputSize="md"
                                            maxValue={2000}
                                            labelText={userSettings.currentRegion === RegionsEnum.Am
                                                ? MESSAGES.DamperWidthAm
                                                : MESSAGES.DamperWidth}
                                            value={state.width}
                                            shaded={false}
                                            endAdornment={(
                                                <UnitSelector
                                                    selected={userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.Inch.name
                                                        : Units.Millimeter.name}
                                                    shaded={false}
                                                    values={[
                                                        userSettings.currentRegion === RegionsEnum.Am
                                                            ? Units.Inch
                                                            : Units.Millimeter,
                                                    ]}
                                                    onChange={() => null}
                                                />
                                            )}
                                            currentLocale={userSettings.currentLocale}
                                            onValueChange={(payload) => {
                                                areaFieldResult(payload, state.height);
                                            }}
                                        />

                                        <DecimalField
                                            labelText={MapFieldToLabel.areaOrDiameter}
                                            labelPostfix={userSettings.currentRegion !== RegionsEnum.Am ? (
                                                <Tooltip title="" value={<AreaTooltipContent/>} direction="top">
                                                    <div className="vss-actuators-user-input-page__icon-wrapper">
                                                        <InfoIcon/>
                                                    </div>
                                                </Tooltip>
                                            ) : null}
                                            maxValue={userSettings.currentRegion === RegionsEnum.Am ? undefined : 4}
                                            inputSize="md"
                                            name="areaOrDiameter"
                                            value={state.areaOrDiameter}
                                            currentLocale={userSettings.currentLocale}
                                            shaded={false}
                                            endAdornment={(
                                                <UnitSelector
                                                    shaded={false}
                                                    selected={userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.SquareFeet.name
                                                        : Units.SquareMeter.name}
                                                    values={[userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.SquareFeet
                                                        : Units.SquareMeter]}
                                                    onChange={() => null}
                                                />
                                            )}
                                            onValueChange={(payload) => {
                                                setInputState({
                                                    ...state,
                                                    width: '',
                                                    height: '',
                                                    areaOrDiameter: payload,
                                                    force: null,
                                                });
                                            }}
                                        />
                                    </>
                                )}

                                {state.damper === DamperType.Round &&
                                    userSettings.currentRegion !== RegionsEnum.Am &&
                                        (
                                            <DecimalField
                                                labelText={MESSAGES.DamperDiameter}
                                                inputSize="md"
                                                name="areaOrDiameter"
                                                value={state.areaOrDiameter}
                                                currentLocale={userSettings.currentLocale}
                                                shaded={false}
                                                endAdornment={(
                                                    <UnitSelector
                                                        shaded={false}
                                                        selected={Units.Millimeter.name}
                                                        values={[Units.Millimeter]}
                                                        onChange={() => null}
                                                    />
                                                )}
                                                onValueChange={(payload) => {
                                                    setInputState({
                                                        ...state,
                                                        areaOrDiameter: payload,
                                                        force: null,
                                                    });
                                                }}
                                            />
                                        )}

                                {state.damper === DamperType.Round &&
                                    userSettings.currentRegion === RegionsEnum.Am && (
                                    <>
                                        <DecimalField
                                            name="diameter"
                                            inputSize="md"
                                            labelText={MESSAGES.DamperDiameter}
                                            value={state.diameter}
                                            endAdornment={(
                                                <UnitSelector
                                                    shaded={false}
                                                    selected={userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.Inch.name
                                                        : Units.Millimeter.name}
                                                    values={[
                                                        userSettings.currentRegion === RegionsEnum.Am
                                                            ? Units.Inch
                                                            : Units.Millimeter,
                                                    ]}
                                                    onChange={() => null}
                                                />
                                            )}
                                            shaded={false}
                                            currentLocale={userSettings.currentLocale}
                                            onValueChange={diameterFieldResult}
                                        />

                                        <DecimalField
                                            labelText={MapFieldToLabel.areaOrDiameter}
                                            labelPostfix={userSettings.currentRegion !== RegionsEnum.Am ? (
                                                <Tooltip title="" value={<AreaTooltipContent/>} direction="top">
                                                    <div className="vss-actuators-user-input-page__icon-wrapper">
                                                        <InfoIcon/>
                                                    </div>
                                                </Tooltip>
                                            ) : null}
                                            maxValue={userSettings.currentRegion === RegionsEnum.Am ? undefined : 4}
                                            inputSize="md"
                                            name="areaOrDiameter"
                                            value={state.areaOrDiameter}
                                            currentLocale={userSettings.currentLocale}
                                            shaded={false}
                                            endAdornment={(
                                                <UnitSelector
                                                    shaded={false}
                                                    selected={userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.SquareFeet.name
                                                        : Units.SquareMeter.name}
                                                    values={[userSettings.currentRegion === RegionsEnum.Am
                                                        ? Units.SquareFeet
                                                        : Units.SquareMeter]}
                                                    onChange={() => null}
                                                />
                                            )}
                                            onValueChange={(payload) => {
                                                setInputState({
                                                    ...state,
                                                    diameter: '',
                                                    areaOrDiameter: payload,
                                                    force: null,
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </div>

                            {state.damper === DamperType.Rectangular &&
                            (
                                <DropdownField
                                    shaded={false}
                                    renderOption={(val) => val !== null && <I18n messageId={mapBladeTypeNames[val]}/>}
                                    selectedValue={state.blade}
                                    options={damperBlades}
                                    onChange={(value) => {
                                        setInputState({
                                            ...state,
                                            blade: value,
                                            force: null,
                                        });
                                    }}
                                >
                                    <I18n messageId={MapFieldToLabel.blade}/>
                                </DropdownField>
                            )}

                            {state.damper === DamperType.Round && userSettings.currentRegion !== RegionsEnum.Am &&
                            (
                                <DropdownField
                                    shaded={false}
                                    renderOption={(val) => val !== null && (
                                        <I18n
                                            messageId={val === RoundDamperBlades.BladeWithSeals
                                                ? MESSAGES.BladeWithSeals : MESSAGES.BladeWithoutSeals}
                                        />
                                    )}
                                    selectedValue={state.roundDumper}
                                    options={[RoundDamperBlades.BladeWithSeals, RoundDamperBlades.BladeWithoutSeals]}
                                    onChange={(value) => {
                                        setInputState({
                                            ...state,
                                            roundDumper: value,
                                            force: null,
                                        });
                                    }}
                                >
                                    <I18n messageId={MapFieldToLabel.blade}/>
                                </DropdownField>
                            )}

                            {userSettings.currentRegion === RegionsEnum.Am && (
                                <div className="vss-actuators-user-input-page__checkbox-section">
                                    <div className="vss-actuators-user-input-page__checkbox-title">
                                        <I18n messageId={MapFieldToLabel.air}/>

                                        <Tooltip title="" value={<AirflowTooltipContent/>} direction="top">
                                            <div className="vss-actuators-user-input-page__icon-wrapper">
                                                <InfoIcon/>
                                            </div>
                                        </Tooltip>
                                    </div>

                                    <div className="vss-actuators-user-input-page__checkbox-row">
                                        <RadioButton
                                            name="air"
                                            id={`air${AirflowEnum.low}`}
                                            value={AirflowEnum.low}
                                            checked={state.air === AirflowEnum.low}
                                            onChange={() => {
                                                setInputState({
                                                    ...state,
                                                    air: AirflowEnum.low,
                                                    force: null,
                                                });
                                            }}
                                        >
                                            <I18n messageId={MESSAGES.AirflowLow}/>
                                        </RadioButton>

                                        <RadioButton
                                            name="air"
                                            id={`air${AirflowEnum.mid}`}
                                            value={AirflowEnum.mid}
                                            checked={state.air === AirflowEnum.mid}
                                            onChange={() => {
                                                setInputState({
                                                    ...state,
                                                    air: AirflowEnum.mid,
                                                    force: null,
                                                });
                                            }}
                                        >
                                            <I18n messageId={MESSAGES.AirflowMid}/>
                                        </RadioButton>

                                        <RadioButton
                                            name="air"
                                            id={`air${AirflowEnum.high}`}
                                            value={AirflowEnum.high}
                                            checked={state.air === AirflowEnum.high}
                                            onChange={() => {
                                                setInputState({
                                                    ...state,
                                                    air: AirflowEnum.high,
                                                    force: null,
                                                });
                                            }}
                                        >
                                            <I18n messageId={MESSAGES.AirflowHigh}/>
                                        </RadioButton>
                                    </div>
                                </div>
                            )}

                            {userSettings.currentRegion !== RegionsEnum.Am && (
                                <DecimalField
                                    labelText={MapFieldToLabel.safety}
                                    labelPostfix={(
                                        <Tooltip title="" value={<SafetyTooltipContent/>} direction="top">
                                            <div className="vss-actuators-user-input-page__icon-wrapper">
                                                <InfoIcon/>
                                            </div>
                                        </Tooltip>
                                    )}
                                    inputSize="md"
                                    maxValue={100}
                                    name="safety"
                                    value={state.safety}
                                    currentLocale={userSettings.currentLocale}
                                    shaded={false}
                                    endAdornment={(
                                        <UnitSelector
                                            shaded={false}
                                            selected={Units.Percent.name}
                                            values={[Units.Percent]}
                                            onChange={() => null}
                                        />
                                    )}
                                    onValueChange={(payload) => {
                                        setInputState({
                                            ...state,
                                            safety: payload,
                                            force: null,
                                        });
                                    }}
                                />
                            )}

                            {selectedActuatorType === ActuatorTypes.Linear && (
                                <DecimalField
                                    labelText={MapFieldToLabel.crankArmLength}
                                    inputSize="md"
                                    name="crankArmLength"
                                    value={state.crankArmLength}
                                    currentLocale={userSettings.currentLocale}
                                    shaded={false}
                                    endAdornment={(
                                        <UnitSelector
                                            shaded={false}
                                            selected={userSettings.currentRegion === RegionsEnum.Am
                                                ? Units.Inch.name
                                                : Units.Millimeter.name}
                                            values={[userSettings.currentRegion === RegionsEnum.Am
                                                ? Units.Inch
                                                : Units.Millimeter]}
                                            onChange={() => null}
                                        />
                                    )}
                                    onValueChange={(payload) => {
                                        setInputState({
                                            ...state,
                                            crankArmLength: payload,
                                            force: null,
                                        });
                                    }}
                                />
                            )}

                        </CalculatorLeftColumn>
                        {userSettings.currentRegion === RegionsEnum.Am && (
                            <CalculatorRightColumn>
                                <DecimalField
                                    labelText={selectedActuatorType === ActuatorTypes.Linear
                                        ? MESSAGES.DamperRequiredForce : MESSAGES.DamperRequiredTorque}
                                    inputSize="md"
                                    name="force"
                                    value={state.force === null ? state.result : state.force}
                                    currentLocale={userSettings.currentLocale}
                                    shaded={false}
                                    endAdornment={(
                                        <UnitSelector
                                            shaded={false}
                                            selected={targetUnit.name}
                                            values={[targetUnit]}
                                            onChange={() => null}
                                        />
                                    )}
                                    onValueChange={(payload) => {
                                        setInputState({
                                            ...state,
                                            force: payload,
                                        });
                                    }}
                                />
                            </CalculatorRightColumn>
                        )}
                        {userSettings.currentRegion !== RegionsEnum.Am && state.damper !== DamperType.Round && (
                            <CalculatorRightColumn>
                                <div className="vss-actuators-user-input-page__result">
                                    <p className="vss-actuators-user-input-page__result-label">
                                        <I18n
                                            messageId={selectedActuatorType === ActuatorTypes.Linear
                                                ? MESSAGES.DamperRequiredForce : MESSAGES.DamperRequiredTorque}
                                        />
                                    </p>

                                    {state.result.length > 0 && (
                                        <p className="vss-actuators-user-input-page__result-value">
                                            {numberFormatter(
                                                state.result,
                                                userSettings.currentLocale,
                                                ACTUATORS_DECIMAL_DIGITS,
                                            )} <I18n
                                                messageId={targetUnit.name}
                                            />
                                        </p>
                                    )}
                                </div>
                            </CalculatorRightColumn>
                        )}
                    </CalculatorLayout>
                </div>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
